import {
  faInstagram,
  faLinkedin,
  faSquareFacebook,
  faTiktok,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import Image from 'next/image';
import Link from 'next/link';
import type { Route } from 'nextjs-routes';
import type { FC } from 'react';

import ProductHunt from '@/assets/landing/product-hunt.png';
import LogoCVWhite from '@/assets/logo-mindpal.svg';

import Icon from '../Icon';
import StarRating from '../StarRating';
import Text from '../Text';
import type { NavLink } from './Header/navigation';
import { navigation } from './Header/navigation';

const terms = [
  { name: 'Privacy Policy', href: '/privacy-policy' },
  { name: 'Cookie Policy', href: '/cookie-policy' },
  { name: 'Terms of Service', href: '/terms-of-service' },
  { name: 'Responsible Disclosure', href: '/responsible-disclosure' },
  { name: 'SMS Terms', href: '/sms-terms' },
] as const;

const socials = [
  { icon: faInstagram, href: 'https://www.instagram.com/mindpal.co/' },
  { icon: faSquareFacebook, href: 'https://www.facebook.com/mindpal.co' },
  {
    icon: faLinkedin,
    href: 'https://www.linkedin.com/company/mindpal-co/posts/?feedView=all',
  },
  { icon: faTiktok, href: 'https://www.tiktok.com/@mindpal.co' },
  { icon: 'pajamas:twitter', href: 'https://x.com/mindpal_co' },
  { icon: faYoutube, href: 'https://www.youtube.com/@mindpalco' },
];

const Footer = () => {
  return (
    <div className="flex w-full flex-col gap-16 bg-jb-primary-600 p-10 lg:px-28 lg:py-10">
      <div className="flex flex-col justify-between gap-10 lg:flex-row">
        <div className="flex max-w-md flex-col gap-4">
          <Text variant="jb-h3">Let&apos;s build innovation together.</Text>
          <a href="mailto:hello@mindpal.co">
            <Text>hello@mindpal.co</Text>
          </a>
          <a
            href="https://www.google.com/maps/place/MindPal/@37.7881648,-122.404151,766m/data=!3m1!1e3!4m16!1m9!3m8!1s0x8085808857f525c3:0xa5c315c31ad3aadc!2s28+Geary+St+%23650,+San+Francisco,+CA+94108,+Stany+Zjednoczone!3b1!8m2!3d37.7881648!4d-122.404151!10e3!16s%2Fg%2F11q_651n4m!3m5!1s0x8085813c3e4b73c7:0x89aa81135f30efb3!8m2!3d37.7881648!4d-122.404151!16s%2Fg%2F11ntnwhp13?entry=ttu&g_ep=EgoyMDI2MDgxNi4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="w-fit"
          >
            <Text variant="jb-body-small">
              28 Geary, Suite 650
              <br />
              San Francisco, 94108 California
            </Text>
          </a>
          <div className="flex flex-row items-center justify-start gap-2">
            {socials.map(({ href, icon }) => (
              <a
                href={href}
                key={href}
                target="_blank"
                className="flex aspect-square w-10 items-center justify-center rounded-full transition-colors hover:text-jb-primary-400 lg:w-10"
              >
                <Icon icon={icon} className="lg:!text-2xl" />
              </a>
            ))}
          </div>
        </div>
        <div className="flex w-full flex-col gap-16 xl:max-w-2xl 2xl:max-w-4xl">
          <FooterNavigation />
        </div>
      </div>
      <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-center">
        <Link
          href="/"
          aria-label="MindPal – go to homepage"
          className="w-48 cursor-pointer transition-transform hover:scale-105"
        >
          <LogoCVWhite />
        </Link>
        <a
          href="https://www.producthunt.com/products/mindpal"
          target="_blank"
          className="w-fit transition-transform hover:scale-105"
        >
          <Image
            src={ProductHunt}
            alt="Product Hunt Logo"
            className="w-40 object-contain object-center"
          />
          <div className="flex flex-row items-center justify-center gap-1">
            <StarRating size={20} className="text-jb-secondary-500" />
            <Text variant="jb-body-medium" className="pl-1">
              5/5
            </Text>
          </div>
        </a>
      </div>
      <div className="flex flex-col gap-2">
        <Text variant="jb-body-small" className="text-jb-neutral-100">
          This site is protected by reCAPTCHA and the Google{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Terms of Service
          </a>{' '}
          apply.
        </Text>
        <div className="flex flex-row items-center justify-between">
          <div className="flex w-full flex-col justify-between gap-4 lg:flex-row lg:gap-0">
            <Text variant="jb-body-small">
              Copyright © MindPal {new Date().getFullYear().toString()}. All
              Rights Reserved.
            </Text>
            <div className="flex flex-col gap-5 underline lg:flex-row lg:gap-0 lg:divide-x">
              {terms.map(({ name, href }) => (
                <Link
                  href={href}
                  key={name}
                  className="lg:px-3"
                  target="_blank"
                >
                  <Text variant="jb-body-small" className="whitespace-pre-line">
                    {name}
                  </Text>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;

const FooterLink: FC<{ link: NavLink }> = ({ link }) => {
  if (link.href === '#' && !link.contact) {
    return (
      <div className="group relative w-fit">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="#" onClick={(e) => e.preventDefault()}>
          <Text variant="jb-body-small">{link.label}</Text>
        </a>
        <span className="absolute bottom-[-4px] left-0 h-[2px] w-full origin-center scale-x-0 rounded-full bg-white transition-all duration-300 group-hover:scale-x-100" />
      </div>
    );
  }

  if (link.external) {
    return (
      <div className="group relative w-fit">
        <Link
          href={link.href as unknown as Route}
          className="inline-flex items-center gap-1"
        >
          <Text variant="jb-body-small" className="underline">
            {link.label}
          </Text>
          <Icon fontSize={16} icon="lsicon:open-new-filled" />
        </Link>
        <span className="absolute bottom-[-4px] left-0 h-[2px] w-full origin-center scale-x-0 rounded-full bg-white transition-all duration-300 group-hover:scale-x-100" />
      </div>
    );
  }

  const href = (link.contact
    ? '/?contact=true'
    : link.href) as unknown as Route;

  return (
    <div className="group relative w-fit">
      <Link href={href}>
        <Text variant="jb-body-small">{link.label}</Text>
      </Link>
      <span className="absolute bottom-[-4px] left-0 h-[2px] w-full origin-center scale-x-0 rounded-full bg-white transition-all duration-300 group-hover:scale-x-100" />
    </div>
  );
};

const FooterNavigation = () => {
  return (
    <div className="flex w-full flex-col justify-end gap-10 lg:flex-row lg:gap-4">
      {navigation.map((item) => {
        const groups = item.rows?.flat() ?? [];
        const links = item.href
          ? [{ label: item.label, href: item.href }]
          : groups.flatMap((g) => g.links);

        return (
          <div key={item.label} className="flex flex-1 flex-col gap-6">
            <Text variant="jb-body-medium" className="font-semibold">
              {item.label.toLocaleUpperCase()}
            </Text>
            <div className="flex flex-col gap-4">
              {links.map((link) => (
                <FooterLink key={`${link.href}-${link.label}`} link={link} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
